import styled from 'styled-components';
import { FlexView, Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

import {
  SA_COLORS,
  ThreadContainer,
  ThreadScroll,
  ThreadInner,
  DateSeparator,
  DateSeparatorLabel,
  TicketCardHead,
} from 'screens/support-shared/styled-comps';

export {
  SA_COLORS,
  ThreadContainer,
  ThreadScroll,
  ThreadInner,
  DateSeparator,
  DateSeparatorLabel,
  TicketCardHead,
};

const SA = SA_COLORS;

// ── Main Layout ──────────────────────────────────────────────────────
export const Workspace = styled(FlexView)`
  && {
    display: grid;
    grid-template-columns: 360px 1fr;
    grid-template-rows: 1fr;
    height: calc(100vh - 56px);
    background: ${SA.BG_PRIMARY};
  }
`;

// ── Search Section ───────────────────────────────────────────────────
export const TopBar = styled(FlexView)`
  && {
    padding: 18px 16px 16px;
    border-bottom: 1px solid ${SA.BORDER_TERTIARY};
    background: ${SA.BG_SECONDARY};
  }
`;

export const SectionLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${SA.TEXT_TERTIARY};
  margin-bottom: 10px;
`;

export const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  font-size: 13px;
  border: 1px solid ${SA.BORDER_SECONDARY};
  background: white;
  color: ${SA.TEXT_PRIMARY};
  outline: none;

  &:focus {
    border-color: ${SA.GOLD_500};
  }

  &::placeholder {
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const BtnPrimary = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  background: ${SA.INFO_900};
  color: white;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const BtnSecondary = styled(BtnPrimary)`
  background: transparent;
  color: ${SA.INFO_900};
  border: 1px solid ${SA.BORDER_SECONDARY};

  &:hover {
    background: ${SA.BG_TERTIARY};
  }
`;

export const CreateBtn = styled(BtnSecondary)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

// ── Left Panel (ticket list) ─────────────────────────────────────────
export const LeftPanel = styled(FlexView)`
  && {
    border-right: 1px solid ${SA.BORDER_TERTIARY};
    min-height: 0;
    background: ${SA.BG_SECONDARY};
  }
`;

export const TicketListScroll = styled(FlexView)`
  && {
    flex: 1;
    overflow: auto;
    padding: 8px;
    gap: 6px;
    min-height: 0;

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${SA.BORDER_TERTIARY};
      border: 3px solid ${SA.BG_SECONDARY};
      border-radius: 9999px;
    }
  }
`;

// ── User Header (shown after search) ────────────────────────────────
export const UserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px 16px;
  border-bottom: 1px solid ${SA.BORDER_TERTIARY};
`;

export const Avatar = styled.div`
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 9999px;
  background: ${SA.GOLD_50};
  border: 1px solid ${SA.GOLD_200};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: ${SA.GOLD_500};
`;

export const UserHeaderInfo = styled.div`
  min-width: 0;
`;

export const UserHeaderName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${SA.TEXT_PRIMARY};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserHeaderMeta = styled.div`
  font-size: 12px;
  color: ${SA.TEXT_TERTIARY};
  margin-top: 1px;
`;

// ── Create Footer ───────────────────────────────────────────────────
export const CreateFooter = styled.div`
  flex: 0 0 auto;
  padding: 14px 16px;
  border-top: 1px solid ${SA.BORDER_TERTIARY};
  background: ${SA.BG_SECONDARY};
`;

export const CreateHint = styled.div`
  font-size: 11px;
  color: ${SA.TEXT_TERTIARY};
  margin-bottom: 8px;
`;

// ── Create Form (full-screen "new ticket" view) ─────────────────────
export const CreateFormPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background: ${SA.BG_PRIMARY};
`;

export const CreateFormInner = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 40px;
`;

export const CreateFormTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${SA.TEXT_PRIMARY};
  margin-bottom: 4px;
`;

export const CreateFormSub = styled.div`
  font-size: 13px;
  color: ${SA.TEXT_TERTIARY};
  margin-bottom: 28px;
`;

export const CreateFormInput = styled(SearchInput)`
  width: 100%;
`;

export const CreateFormError = styled.div`
  font-size: 12px;
  color: ${SA.ERROR_600};
  margin-top: 12px;
`;

export const CreateFormActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid ${SA.BORDER_TERTIARY};
`;

export const RoleChoiceCard = styled.div`
  margin-top: 18px;
  padding: 14px;
  border: 1px solid ${SA.BORDER_TERTIARY};
  background: ${SA.BG_SECONDARY};
`;

export const RoleChoiceTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${SA.TEXT_PRIMARY};
  margin-bottom: 3px;
`;

export const RoleChoiceSub = styled.div`
  font-size: 12px;
  color: ${SA.TEXT_TERTIARY};
  margin-bottom: 12px;
`;

export const RoleChoiceRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const RolePill = styled.button`
  flex: 1 1 0;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 12px;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease, border-color 150ms ease;
  background: ${({ selected }) => (selected ? SA.GOLD_50 : SA.BG_PRIMARY)};
  color: ${({ selected }) => (selected ? SA.GOLD_900 : SA.TEXT_SECONDARY)};
  border: 1px solid ${({ selected }) => (selected ? SA.GOLD_500 : SA.BORDER_SECONDARY)};
`;

export const TicketCard = styled(FlexView)`
  && {
    padding: 12px 14px;
    border: 1px solid ${({ $active }) => ($active ? SA.GOLD_500 : SA.BORDER_TERTIARY)};
    background: ${({ $active }) => ($active ? SA.GOLD_50 : SA.BG_PRIMARY)};
    cursor: pointer;
    gap: 6px;

    &:hover {
      background: ${({ $active }) => ($active ? SA.GOLD_50 : SA.BG_TERTIARY)};
    }
  }
`;

export const TicketStatus = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $closed }) => ($closed ? SA.TEXT_SECONDARY : '#00B259')};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $closed }) => ($closed ? '#EC5B13' : '#00B259')};
  }
`;

export const TicketTime = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA.TEXT_TERTIARY};
    margin-left: auto;
  }
`;

export const TicketPreviewLine = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $closed }) => ($closed ? SA.TEXT_SECONDARY : SA.TEXT_PRIMARY)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TicketId = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA.TEXT_TERTIARY};
    font-family: monospace;
  }
`;

// ── Thread Header ───────────────────────────────────────────────────
export const ThreadHeaderBar = styled(Row)`
  && {
    padding: 14px 24px;
    border-bottom: 1px solid ${SA.BORDER_TERTIARY};
    gap: 12px;
    align-items: center;
  }
`;

export const ThreadHeaderTitle = styled(FlexView)`
  && {
    flex: 1;
  }
`;

export const ThreadHeaderStatus = styled(Text)`
  && {
    color: ${SA.TEXT_TERTIARY};
    display: block;
  }
`;

// ── Composer ─────────────────────────────────────────────────────────
export const ComposerWrap = styled(FlexView)`
  && {
    border-top: 1px solid ${SA.BORDER_TERTIARY};
    background: white;
  }
`;

export const ComposerTextarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  font-size: 13px;
  line-height: 1.5;
  border: none;
  resize: none;
  outline: none;
  font-family: inherit;
  min-height: 60px;
  max-height: 140px;
  color: ${SA.TEXT_PRIMARY};

  &::placeholder {
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const ComposerRow = styled(Row)`
  && {
    padding: 6px 14px 10px;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
`;

export const ComposerHint = styled(Text)`
  && {
    flex: 1;
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const SendBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${SA.GOLD_500};
  border: none;
  cursor: pointer;
  color: white;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

// ── Empty States ─────────────────────────────────────────────────────
export const EmptyState = styled(FlexView)`
  && {
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex: 1;
    padding: 40px;
  }
`;

export const EmptyIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 9999px;
  background: ${SA.BG_TERTIARY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${SA.TEXT_TERTIARY};
  margin-bottom: 4px;
`;

export const EmptyTitle = styled(Text)`
  && {
    font-size: 16px;
    font-weight: 500;
    color: ${SA.TEXT_SECONDARY};
  }
`;

export const EmptySub = styled(Text)`
  && {
    font-size: 13px;
    color: ${SA.TEXT_TERTIARY};
    text-align: center;
  }
`;

export const EmptySubNarrow = styled(EmptySub)`
  && {
    max-width: 220px;
  }
`;

// ── Error / Info ─────────────────────────────────────────────────────
export const ErrorMsg = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA.ERROR_600};
    padding: 8px 16px;
    background: ${SA.ERROR_50};
    border-top: 1px solid ${SA.ERROR_200};
    text-align: center;
  }
`;

// ── No Access ────────────────────────────────────────────────────────
export const NoAccessWrap = styled(FlexView)`
  && {
    align-items: center;
    justify-content: center;
    height: calc(100vh - 56px);
    gap: 16px;
    background: ${SA.BG_PRIMARY};
  }
`;

// ── Load Older Button ───────────────────────────────────────────────
export const LoadOlderBtn = styled.button`
  display: block;
  margin: 8px auto 16px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${SA.INFO_600};
  background: ${SA.BG_SECONDARY};
  border: 1px solid ${SA.BORDER_SECONDARY};
  cursor: pointer;

  &:hover {
    background: ${SA.BG_TERTIARY};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// ── Modal ───────────────────────────────────────────────────────────
export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

export const ModalTitle = styled(Text)`
  && {
    font-size: 16px;
    font-weight: 700;
    color: ${SA.TEXT_PRIMARY};
    margin-bottom: 12px;
  }
`;

export const ModalBody = styled(Text)`
  && {
    font-size: 14px;
    line-height: 1.6;
    color: ${SA.TEXT_SECONDARY};
    margin-bottom: 20px;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
